<script lang="ts">
	import { superForm, defaults } from "sveltekit-superforms";
	import { zod4 } from "sveltekit-superforms/adapters";
	import { newsletterSignupFormClientSchema } from "./newsletter-signup-popup.schema";
	import { trpcBrowser } from "#trpc/client.svelte.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import { toast } from "svelte-sonner";
	import { onMount } from "svelte";
	import { browser } from "$app/env";
	import * as Card from "#lib/web-ui/ui/card/index.js";
	import XIcon from "@lucide/svelte/icons/x";
	import MailIcon from "@lucide/svelte/icons/mail";

	let { constantContactListId }: { constantContactListId: string } = $props();

	let showPopup = $state(false);

	const cookieName = "fergmar-com-newsletter-popup-seen";
	const cookieExpiry = 90;

	const hasSeenPopup = $derived(getCookie(cookieName) === "true");

	function setCookie(name: string, value: string, days: number) {
		if (!browser) return;
		const date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		const expires = `expires=${date.toUTCString()}`;
		document.cookie = `${name}=${value};${expires};path=/`;
	}

	function getCookie(name: string): string | null {
		if (!browser) return null;
		const nameEq = `${name}=`;
		const cookies = document.cookie.split(";");
		for (let i = 0; i < cookies.length; i++) {
			let cookie = cookies[i].trim();
			if (cookie.indexOf(nameEq) === 0) {
				return cookie.substring(nameEq.length, cookie.length);
			}
		}
		return null;
	}

	function closePopup() {
		showPopup = false;
		setCookie(cookieName, "true", cookieExpiry);
	}

	function pushToDataLayer(eventName: string) {
		if (typeof window !== "undefined" && window.dataLayer) {
			window.dataLayer.push({
				event: eventName,
				formId: "newsletter_signup",
				pagePath: window.location.pathname
			});
		}
	}

	const form = superForm(defaults(zod4(newsletterSignupFormClientSchema)), {
		id: "newsletter-signup",
		SPA: true,
		resetForm: true,
		validators: zod4(newsletterSignupFormClientSchema),
		async onUpdate({ form }) {
			if (!form.valid) return;

			try {
				await trpcBrowser.public.forms.submitNewsletterSignup.mutate({
					...form.data,
					constantContactListId
				});
			} catch (e) {
				toast.error(
					e instanceof Error ? e.message : "Error subscribing. Please try again later."
				);
				pushToDataLayer("form_submission_error");
				return;
			}

			toast.success("Successfully subscribed to newsletter!");
			pushToDataLayer("form_submission_success");
			setCookie(cookieName, "true", cookieExpiry);

			setTimeout(() => {
				showPopup = false;
			}, 2000);
		}
	});

	const { form: formData, enhance, errors, submitting } = form;

	onMount(() => {
		if (!hasSeenPopup) {
			setTimeout(() => {
				showPopup = true;
			}, 2000);
		}
	});
</script>

{#if showPopup}
	<!-- Overlay -->
	<div
		class="fixed inset-0 z-50 bg-black/50 transition-opacity duration-200 ease-in-out
                {showPopup ? 'opacity-100' : 'pointer-events-none opacity-0'}"
	>
		<!-- Popup Card -->
		<div
			class="fixed top-1/2 left-1/2 z-50 w-full max-w-md
                  -translate-x-1/2 -translate-y-1/2 transform p-4"
		>
			<Card.Card class="border-2 shadow-lg">
				<Card.CardHeader class="pb-2">
					<div class="flex items-center justify-between">
						<div class="flex items-center gap-2">
							<MailIcon class="text-primary h-5 w-5" />
							<Card.CardTitle class="text-xl">Join Our Mailing List</Card.CardTitle>
						</div>
						<Button variant="ghost" size="icon" onclick={closePopup} class="h-8 w-8">
							<XIcon class="h-4 w-4" />
						</Button>
					</div>
					<Card.CardDescription>
						Get the latest updates on new properties and investment offerings.
					</Card.CardDescription>
				</Card.CardHeader>

				<Card.CardContent>
					<form use:enhance method="POST" class="space-y-4">
						<div>
							<Input
								name="email"
								type="email"
								bind:value={$formData.email}
								autocomplete="email"
								placeholder="Enter your email"
								class="w-full"
								aria-label="Email address"
							/>
							{#if $errors.email}
								<p class="text-destructive mt-1 text-xs">{$errors.email}</p>
							{/if}
						</div>

						<div class="flex space-x-2">
							<Button
								type="button"
								variant="outline"
								class="flex-1"
								onclick={closePopup}>No thanks</Button
							>

							<Button type="submit" class="flex-1" disabled={$submitting}>
								{#if $submitting}
									<div
										class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
									></div>
									Joining...
								{:else}
									Subscribe
								{/if}
							</Button>
						</div>
					</form>
				</Card.CardContent>
			</Card.Card>
		</div>
	</div>
{/if}
